import styled from 'styled-components';
import { exFont } from '../../shared/typography';
import { StepPage, StepHead } from '../YourMarksStep/style';

// Samples (tplA 1117–1144, calMulti) and the graders the drawer reuses.

export const SamplesPage = styled(StepPage)`
  max-width: none;
  padding: 0 0 40px;
  gap: 0;
`;

export const SamplesHead = styled(StepHead)`
  padding: 18px 24px 0;
  background: var(--ex-surface);
`;

export const Progress = styled.span`
  ${exFont(600, 's')}
  padding: 14px 0 0;
  color: var(--ex-ink);
  white-space: nowrap;
`;

export const TabsBar = styled.div`
  padding: 12px 24px 14px;
  background: var(--ex-surface);
  border-bottom: 1px solid var(--ex-line);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const QuestionTab = styled.button`
  ${exFont(500, 's')}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: var(--ex-radius-pill);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple-100)' : 'var(--ex-surface)'};
  color: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-ink-strong)'};
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const Dots = styled.span`
  display: inline-flex;
  gap: 3px;
`;

export const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $done }) =>
    $done ? 'var(--ex-ok)' : 'var(--ex-line-strong)'};
`;

export const Spacer = styled.span`
  flex: 1 1 auto;
`;

export const Workspace = styled.div`
  min-height: calc(100vh - 264px);
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: 0;
  align-items: stretch;
  & > [data-testid='sample-viewer'] {
    padding: 20px 24px;
    border: 0;
    border-radius: 0;
    background: var(--ex-surface-2);
    box-shadow: none;
  }
  & > [data-testid='sample-grader'] {
    padding: 20px;
    border: 0;
    border-left: 1px solid var(--ex-line);
    border-radius: 0;
    box-shadow: none;
  }
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
    & > [data-testid='sample-grader'] {
      border-left: 0;
      border-top: 1px solid var(--ex-line);
    }
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  box-shadow: var(--ex-shadow-1);
  min-width: 0;
`;

export const ViewerHead = styled.div`
  [role='radiogroup'] {
    border: 1px solid var(--ex-line-strong);
    background: var(--ex-surface);
  }
  [role='radio'][aria-checked='true'] {
    background: var(--ex-purple);
    color: var(--white);
  }

  ${exFont(500, 's')}
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: var(--ex-ink-2);
`;

export const ViewerLabel = styled.span`
  ${exFont(700, 's')}
  padding: 4px 10px;
  border-radius: var(--ex-radius-pill);
  color: var(--white);
  background: var(--ex-purple);
`;

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 50%;
  background: var(--ex-surface);
  color: var(--ex-ink-strong);
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const QuestionText = styled.div`
  ${exFont(400, 'm')}
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-tint);
  color: var(--ex-ink);
  line-height: 1.5;
  p {
    margin: 0;
  }
`;

export const SampleQuestion = styled(QuestionText)`
  padding: 12px 16px;
  border: 1px solid var(--ex-line);
  border-radius: 12px;
  background: var(--ex-surface);
`;

export const StimulusLine = styled.p`
  ${exFont(400, 's')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const ResponseText = styled.div`
  ${exFont(400, 'm')}
  color: var(--ex-ink);
  line-height: 1.6;
  white-space: pre-wrap;
  p {
    margin: 0 0 4px;
  }
`;

export const PageLabel = styled.figcaption`
  ${exFont(600, 'xs')}
  margin-bottom: 4px;
  color: var(--ex-ink-muted);
`;

export const PageImage = styled.img`
  display: block;
  width: 100%;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-2);
`;

export const Notice = styled.p`
  ${exFont(500, 's')}
  margin: 0;
  padding: 16px;
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-2);
  color: var(--ex-ink-2);
`;

export const GraderHead = styled.p`
  ${exFont(600, 'm')}
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0 0 8px;
  color: var(--ex-ink);
`;

export const GraderHint = styled.span`
  ${exFont(400, 's')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const Criterion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid var(--ex-line);
`;

export const CriterionName = styled.p`
  ${exFont(600, 's')}
  margin: 0;
  color: var(--ex-ink-strong);
`;

export const LevelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const LevelButton = styled.button`
  ${exFont(700, 's')}
  min-width: 34px;
  height: 30px;
  padding: 0 8px;
  border-radius: var(--ex-radius-item);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
  color: ${({ $active }) =>
    $active ? 'var(--white)' : 'var(--ex-ink-strong)'};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LevelSelected = styled.p`
  ${exFont(400, 'xs')}
  margin: 0;
  color: var(--ex-ink-2);
  line-height: 1.45;
`;

export const MarkRow = styled.div`
  ${exFont(600, 's')}
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: var(--ex-ink-strong);
`;

export const MarkInput = styled.input`
  ${exFont(600, 'm')}
  width: 64px;
  padding: 6px 8px;
  text-align: center;
  border: 1px solid
    ${({ $over, $warn }) =>
      $over
        ? 'var(--ex-bad)'
        : $warn
        ? 'var(--ex-warn)'
        : 'var(--ex-line-strong)'};
  border-radius: var(--ex-radius-item);
  color: ${({ $over }) => ($over ? 'var(--ex-bad-ink)' : 'var(--ex-ink)')};
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

export const OverMax = styled.span`
  ${exFont(500, 'xs')}
  color: var(--ex-bad-ink);
`;

export const Warning = styled.p`
  ${exFont(500, 's')}
  margin: 8px 0 0;
  padding: 8px 12px;
  border-left: 3px solid var(--ex-warn);
  border-radius: var(--ex-radius-item);
  color: var(--ex-warn-ink);
  background: var(--ex-warn-bg);
`;

export const GuideRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin-bottom: 6px;
  padding: 10px 12px;
  text-align: left;
  border-radius: var(--ex-radius-item);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple-100)' : 'var(--ex-surface)'};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
`;

export const GuideMark = styled.span`
  ${exFont(700, 'm')}
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ex-purple)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-purple-100)'};
`;

export const GuideText = styled.span`
  ${exFont(400, 's')}
  color: var(--ex-ink);
  line-height: 1.45;
`;

export const PanelFoot = styled.div`
  margin-top: auto;
  ${exFont(600, 's')}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--ex-line);
  color: var(--ex-ink-strong);
`;

export const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
`;

export const WaitingTitle = styled.p`
  ${exFont(600, 'xl')}
  margin: 0;
`;

export const WaitingBody = styled.p`
  ${exFont(400, 'm')}
  margin: 0;
  max-width: 520px;
  color: var(--ex-ink-2);
  line-height: 1.5;
`;

export const SamplesLayout = styled(SamplesPage)`
  h2 {
    ${exFont(700, 21)}
  }
  ${QuestionTab} {
    ${exFont(600, 13.5)}
    height: 36px;
    border-radius: 10px;
    border-width: 1.5px;
  }
  ${TabsBar} button[title='Mark another sample for this question'] {
    height: 36px;
    ${exFont(600, 13.5)}
    padding: 0 14px;
    color: var(--ex-purple);
    border: 1.5px dashed var(--ex-purple-200);
    border-radius: 10px;
    background: var(--ex-surface);
    > span {
      font-weight: 500;
      color: var(--ex-ink-muted);
    }
  }
  ${Workspace} {
    grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
  }
  ${Workspace} > [data-testid='sample-viewer'] [data-testid='typed-response'] {
    min-height: 240px;
    padding: 24px 28px;
    border: 1px solid var(--ex-line);
    border-radius: 12px;
    background: var(--ex-surface);
    font: 400 16px/1.8 Georgia, serif;
  }
  ${Workspace} > [data-testid='sample-grader'] ${GraderHead} {
    ${exFont(700, 16)}
    line-height: normal;
  }
  ${Workspace} > [data-testid='sample-grader'] ${GuideRow} {
    gap: 10px;
    border-width: 1.5px;
    border-radius: 10px;
  }
  ${Workspace} > [data-testid='sample-grader'] ${GuideMark} {
    ${exFont(700, 15)}
    width: 40px;
    border-radius: 8px;
  }
  ${Workspace} > [data-testid='sample-grader'] ${GuideText} {
    ${exFont(400, 13.5)}
    padding-top: 4px;
  }
  ${Workspace} > [data-testid='sample-grader'] ${LevelButton} {
    flex: 1 1 0;
    height: 38px;
    border-radius: 10px;
  }
  ${Workspace} > [data-testid='sample-grader'] ${MarkRow} {
    ${exFont(600, 14)}
    > span:first-child {
      flex: 1;
      ${exFont(700, 15)}
    }
    padding: 12px 14px;
    border: 1px solid var(--ex-line);
    border-radius: 12px;
  }
  ${Workspace} > [data-testid='sample-grader'] ${MarkInput} {
    width: 68px;
    height: 44px;
    box-sizing: border-box;
    ${exFont(800, 19)}
  }
  ${Workspace} > [data-testid='sample-grader'] ${PanelFoot} button {
    ${exFont(600, 14.5)}
    min-height: 44px;
    padding: 0 22px;
  }
  ${NavButton} {
    width: 34px;
    height: 34px;
  }
  @media (max-width: 760px) {
    ${Workspace} {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;
